import { HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  AuthorizationService,
  ChatSettingsService,
  DEVICE_OS_FAMILY,
  IChatConfiguration,
  IDeviceModel,
  PERMISSION_CONTACTSABHADEVICE_CONSTANT,
  PermissionService,
  XDeviceService,
} from '@nx-fe/services';
import { SabhaCall, SabhaRoomType } from '@soti/sabha-components';
import { combineLatest, EMPTY, from, Observable, of } from 'rxjs';
import {
  catchError,
  filter,
  first,
  map,
  shareReplay,
  startWith,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs/operators';
import { XSightRoom } from '../models/xsight-room/xsight-room';
import { isRunning } from '../sabha-utils';
import { SabhaService } from '../sabha.service';
import * as fromSabhaActions from '../store/actions';
import { RemoveMinifiedViewDeviceAction, ShowChatErrorViewAction, StartDeviceChatAction } from '../store/actions';
import { DeviceChatDialogSize, ErrorViewType, SabhaState } from '../store/reducers';
import * as fromSabha from '../store/selectors';
import { compareVersionNumbers } from '../utils';
import { AndroidDetails, PlatformDetails, WindowsDetails } from './platform-details';
import { XsightRoomService } from './xsight-room.service';

@Injectable({
  providedIn: 'root',
})
export class SabhaClientService {
  private chatToggle$: Observable<boolean>;
  public isStandAlone$: Observable<boolean>;
  public showCallView$: Observable<boolean>;
  public callRoom$: Observable<XSightRoom>;
  public minimizedViewDevice$: Observable<IDeviceModel>;
  public shouldAllowChatAndCall$: Observable<boolean>;
  private dialogSizeOnHide$: Observable<DeviceChatDialogSize>;

  private allowedPlatformMap = new Map<string, PlatformDetails>([
    [DEVICE_OS_FAMILY.Android, AndroidDetails.getPlatformSpecifics()],
    [DEVICE_OS_FAMILY.AndroidPlus, AndroidDetails.getPlatformSpecifics()],
    [DEVICE_OS_FAMILY.WindowsDesktop, WindowsDetails.getPlatformSpecifics()],
    [DEVICE_OS_FAMILY.WindowsModernDesktop, WindowsDetails.getPlatformSpecifics()],
    [DEVICE_OS_FAMILY.WindowsPhone, WindowsDetails.getPlatformSpecifics()],
  ]);

  public get isSabhaAllowed$(): Observable<boolean> {
    return this.store.select(fromSabha.isSabhaAllowed).pipe(filter(allowed => allowed !== undefined));
  }

  public get isCallAllowed$(): Observable<boolean> {
    return this.store.select(fromSabha.getCallAllowed);
  }

  public get isVideoAllowed$(): Observable<boolean> {
    return this.store.select(fromSabha.getVideoAllowed);
  }

  public get ChatConfiguration$(): Observable<IChatConfiguration> {
    return this.store.select(fromSabha.getChatConfiguration);
  }

  private _deviceMap = new Map<string, Observable<IDeviceModel>>();
  private _deviceSabhaPermission = new Map<string, Observable<boolean>>();

  constructor(
    public store: Store<SabhaState>,
    public deviceService: XDeviceService,
    private sabhaService: SabhaService,
    private permissionService: PermissionService,
    private chatSettingsService: ChatSettingsService,
    private xsightRoomService: XsightRoomService,
    private authorizationService: AuthorizationService
  ) {
    this.chatToggle$ = this.store.select(fromSabha.getChatToggle);
    this.setRoomRelatedObservables();
  }

  public getChatSettings$(resetCache: boolean = false): Observable<IChatConfiguration> {
    return from(this.chatSettingsService.getConfig(resetCache));
  }

  public canCallDevice$(mcDeviceId?: string): Observable<boolean> {
    return this.sabhaService.canCallDevice$(mcDeviceId);
  }

  public getSabhaAllowedInfo$(device: string | IDeviceModel): Observable<SabhaAllowedInfo> {
    if (!device) {
      throw new Error('Failed to pass device parameter');
    }

    const getSabhaAllowedInfo$ = (deviceModel: IDeviceModel, isSabhaAllowed: boolean, hasPermission: boolean) => {
      const versionAllowed = this._isVersionAllowed(deviceModel);
      const userChatAccess = this.authorizationService.isAllowed('AccessChat');
      return <SabhaAllowedInfo>{
        shouldShow: userChatAccess && deviceModel && isSabhaAllowed && this.allowedPlatformMap.has(deviceModel.family),
        isEnabled: versionAllowed && hasPermission,
        tooltip: this._getTooltip(deviceModel.family, versionAllowed, hasPermission),
      };
    };

    const device$ = this._getDevice$(device);
    return combineLatest([this.isSabhaAllowed$, device$]).pipe(
      filter(([_, device]) => !!device),
      switchMap(([isAllowed, deviceModel]) => {
        return this._getDeviceSabhaPermission$(deviceModel.mcDeviceId).pipe(
          map(hasPermission => {
            return getSabhaAllowedInfo$(deviceModel, isAllowed, hasPermission);
          })
        );
      })
    );
  }

  public updateChatConfiguration(config: IChatConfiguration) {
    this.store.dispatch(new fromSabhaActions.ChatConfigLoadSuccessAction(config));
  }

  public startSabhaSession(device: string | IDeviceModel, startType: SabhaRoomType) {
    if (!device) {
      return;
    }

    this._getDevice$(device)
      .pipe(first())
      .subscribe(
        deviceModel => {
          this._startDeviceChat(deviceModel, startType);
        },
        err => {
          if (err.status === HttpStatusCode.UnprocessableEntity && isLicenseExpiredError(err.error)) {
            this.store.dispatch(new ShowChatErrorViewAction(ErrorViewType.LicenseExpiredError));
          }
        }
      );
  }

  public removeSabhaMinifiedView(): void {
    this.store.dispatch(new RemoveMinifiedViewDeviceAction());
  }

  /*
   * Show chat popup. Optionally change size
   * If size not provided, current size will be maintained
   */
  public showChatPopup(changeSize?: DeviceChatDialogSize) {
    this.store.dispatch(new fromSabhaActions.ToggleChatAction(true, changeSize));
  }

  /*
   * Show chat popup. It will try to change size to previous size before hide
   */
  public showChatPopupOnPreviousState() {
    // Restore size to the one set before hide
    this.dialogSizeOnHide$
      .pipe(take(1), withLatestFrom(this.isStandAlone$))
      .subscribe(([dialogSizeOnHide, isStandAlone]) => {
        let size = DeviceChatDialogSize.FullSize;
        if (!isStandAlone) {
          size = dialogSizeOnHide ? dialogSizeOnHide : DeviceChatDialogSize.FullSize;
        }
        this.store.dispatch(new fromSabhaActions.ToggleChatAction(true, size));
      });
  }

  /*
   * Hide chat popup. Size will be collapsed
   */
  public hideChatPopup() {
    this.store.dispatch(new fromSabhaActions.ToggleChatAction(false, DeviceChatDialogSize.Collapsed));
  }

  public getToggleChatPopup$(): Observable<boolean> {
    return this.chatToggle$;
  }

  private _getTooltip(osFamily: string, versionAllowed: boolean, hasPermission: boolean): string {
    if (!hasPermission) {
      return 'permissions_insufficient_user_rights';
    }

    if (!versionAllowed) {
      const details = this.allowedPlatformMap.get(osFamily);
      if (!details) {
        return 'chat_InvalidOSVersion_Caption';
      }

      return details.invalidVersionTooltip;
    }

    return 'chat_PopUpTrigger_Caption';
  }

  private _startDeviceChat(device: IDeviceModel, startType: SabhaRoomType): void {
    if (device) {
      this.store.dispatch(new StartDeviceChatAction(device.mcDeviceId, device.deviceName, startType));
    }
  }

  private _getDevice$(device: string | IDeviceModel): Observable<IDeviceModel> {
    const deviceId = typeof device === 'string' ? device : device.mcDeviceId;
    if (!this._deviceMap.has(deviceId)) {
      let device$: Observable<IDeviceModel>;
      if (typeof device === 'string') {
        device$ = this.deviceService.getDevice(device).pipe(shareReplay(1));
      } else {
        device$ = of(device);
      }
      this._deviceMap.set(deviceId, device$);
    }

    return this._deviceMap.get(deviceId);
  }

  private _getDeviceSabhaPermission$(deviceId: string): Observable<boolean> {
    if (!deviceId) {
      return of(false);
    }

    if (!this._deviceSabhaPermission.has(deviceId)) {
      let hasDevicePermission$ = this.permissionService
        .hasDevicePermissionToFeature(deviceId, PERMISSION_CONTACTSABHADEVICE_CONSTANT)
        .pipe(
          shareReplay(1),
          map(result => result?.isAllowed)
        );
      this._deviceSabhaPermission.set(deviceId, hasDevicePermission$);
    }

    return this._deviceSabhaPermission.get(deviceId);
  }

  private _isVersionAllowed(deviceModel: IDeviceModel): boolean {
    if (!deviceModel) {
      return false;
    }

    const platformDetails = this.allowedPlatformMap.get(deviceModel.family);
    if (!platformDetails) {
      return false;
    }

    return compareVersionNumbers(deviceModel.osVersion, platformDetails.allowedVersion) >= 0;
  }

  private setRoomRelatedObservables() {
    this.dialogSizeOnHide$ = this.store.select(fromSabha.getChatDialogSizeOnHide);
    this.callRoom$ = this.xsightRoomService.activeCallRoom$;
    const isCallRinging$ = this.store.select(fromSabha.getRingRoomIds).pipe(
      withLatestFrom(this.callRoom$.pipe(filter(Boolean))),
      map(([ringRoomIds, room]: [string[], XSightRoom]) => ringRoomIds.includes(room.id))
    );
    const isCallRunning$ = this.callRoom$.pipe(
      filter(Boolean),
      switchMap((room: XSightRoom) => room.callContext$),
      map((call: SabhaCall) => isRunning(call))
    );
    this.showCallView$ = combineLatest([isCallRinging$, isCallRunning$]).pipe(
      map(([isCallRinging, isCallRunning]: [boolean, boolean]) => isCallRinging || !!isCallRunning)
    );

    this.minimizedViewDevice$ = this.store.select(fromSabha.getMinifiedDeviceView).pipe(startWith(null));
    this.shouldAllowChatAndCall$ = this.minimizedViewDevice$.pipe(
      filter(d => !!d),
      switchMap(device => this.getSabhaAllowedInfo$(device)),
      catchError(err => {
        console.error(err);
        return EMPTY;
      }),
      map(
        (sabhaAllowedInfo: SabhaAllowedInfo) =>
          !!sabhaAllowedInfo && sabhaAllowedInfo.shouldShow && sabhaAllowedInfo.isEnabled
      ),
      startWith(false),
      shareReplay(1)
    );

    this.isStandAlone$ = combineLatest([
      this.showCallView$.pipe(startWith(false)),
      this.callRoom$.pipe(startWith(null)),
      this.shouldAllowChatAndCall$.pipe(startWith(false)),
      this.minimizedViewDevice$.pipe(startWith(null)),
    ]).pipe(
      map(
        ([showCallView, callRoom, shouldAllowChatAndCall, device]) =>
          !((showCallView && callRoom) || (shouldAllowChatAndCall && device))
      ),
      shareReplay(1)
    );
  }
}

export function getStartChatType(startType: string): SabhaRoomType {
  switch (startType?.toLocaleLowerCase()) {
    case 'audiocall':
      return SabhaRoomType.Call;
    case 'videocall':
      return SabhaRoomType.Video;
    case 'text':
    default:
      return SabhaRoomType.Chat;
  }
}

export interface SabhaAllowedInfo {
  shouldShow: boolean;
  isEnabled: boolean;
  tooltip: string;
}

export interface LicenseExpiredError {
  licenseExpired: boolean;
}

export function isLicenseExpiredError(error: any): error is LicenseExpiredError {
  return error?.licenseExpired;
}
